'use client'

import { useState, type MouseEvent } from 'react'
import Image from 'next/image'
import { Link } from '@/components/site/link'
import { Menu } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import { navigation } from '@/lib/site'
import type { Locale } from '@/types/site'

/**
 * The logo always lands on the homepage hero. From another page its link
 * navigates home, and Next.js scrolls the new page to the top. On the
 * homepage itself a link to the same URL leaves the scroll position alone,
 * so scroll to the top instead and drop a #section (e.g. #what-we-do) from
 * the URL. Modified clicks (new tab, new window) keep the browser default.
 */
export function scrollToHomeHero(event: MouseEvent<HTMLAnchorElement>, home: string) {
  if (window.location.pathname !== home) return
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  event.preventDefault()
  if (window.location.hash) window.history.replaceState(window.history.state, '', home)
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
}

export function SiteHeader({ locale, alternate }: { locale: Locale; alternate: string }) {
  const t = useTranslations('header')
  const tNav = useTranslations('nav')
  const tServices = useTranslations('services')
  const tHome = useTranslations('home')
  const [open, setOpen] = useState(false)
  const es = locale === 'es'
  const links = navigation(locale)
  const services = links.slice(0, 4)
  const meeting = es ? '/es/reunion-de-calendly' : '/calendly-meeting'
  const home = es ? '/es' : '/'
  const whatWeDo = `${home}#what-we-do`

  return (
    <header className="home-header">
      {/* Hoisted to <head>: start the logo request early, at high priority. */}
      <link rel="preload" as="image" href="/site/assets/logo.svg" fetchPriority="high" />
      <div className="home-header-inner">
        <Link
          className="home-logo"
          href={home}
          aria-label={t('home')}
          onClick={(event) => scrollToHomeHero(event, home)}
        >
          <Image
            src="/site/assets/logo.svg"
            alt="Kirana Labs"
            width={142}
            height={29}
            priority
            fetchPriority="high"
          />
        </Link>
        <NavigationMenu className="home-nav">
          <NavigationMenuList className="gap-7">
            <NavigationMenuItem>
              <NavigationMenuTrigger className="home-services-trigger">
                {t('services')}
              </NavigationMenuTrigger>
              <NavigationMenuContent className="home-services-panel-content">
                <div className="home-services-panel">
                  <div className="home-services-intro">
                    <Link href={whatWeDo}>{t('servicesTitle')}</Link>
                    <p>{t('servicesDescription')}</p>
                    <Link href={links[4].href}>{t('exploreCaseStudies')} →</Link>
                  </div>
                  <ul className="home-services-grid">
                    {services.map((link) => (
                      <li key={link.href}>
                        <NavigationMenuLink
                          render={<Link href={link.href} />}
                          className="home-services-card"
                        >
                          <strong>{tNav(link.labelKey)}</strong>
                          <span>{tServices(`${link.labelKey}Summary`)}</span>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
            {links.slice(0, 5).map((link) => (
              <NavigationMenuItem key={link.href}>
                <NavigationMenuLink render={<Link href={link.href} />} className="home-nav-link">
                  {tNav(link.labelKey)}
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>
        <Link href={meeting} className="home-schedule-btn">
          {tHome('hero.ctaPrimary')}
        </Link>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                className="home-menu-trigger"
                aria-label={t('openMenu')}
              />
            }
          >
            <Menu />
          </SheetTrigger>
          <SheetContent className="home-mobile-menu" side="left">
            <SheetHeader>
              <SheetTitle>
                <Link
                  href={home}
                  onClick={(event) => {
                    setOpen(false)
                    scrollToHomeHero(event, home)
                  }}
                  aria-label={t('home')}
                >
                  <Image src="/site/assets/logo.svg" alt="Kirana Labs" width={142} height={29} />
                </Link>
              </SheetTitle>
            </SheetHeader>
            <nav aria-label={t('mobileNav')}>
              <p className="home-mobile-menu-label">{t('services')}</p>
              <ul className="home-mobile-menu-services">
                {services.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href} onClick={() => setOpen(false)}>
                      <strong>{tNav(link.labelKey)}</strong>
                      <span>{tServices(`${link.labelKey}Summary`)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              {links.slice(4).map((link) => (
                <Link key={link.href} href={link.href} onClick={() => setOpen(false)}>
                  {tNav(link.labelKey)}
                </Link>
              ))}
              <Link href={meeting} className="home-mobile-menu-cta" onClick={() => setOpen(false)}>
                {tHome('hero.ctaPrimary')}
              </Link>
              <a
                href={alternate}
                lang={es ? 'en' : 'es'}
                aria-label={es ? 'Switch to English' : 'Cambiar a español'}
              >
                {es ? 'English' : 'Español'}
              </a>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
